<template>
    <jet-dialog-modal :show="show" @close="close">
        <template #title>
            View PDFs
        </template>

        <template #content>
            <div v-if="loading" class="text-center py-8 flex flex-col items-center justify-center">
                <img src="/images/icons/loading-pulse.svg" height="128" width="128" class="" />
                <p class="mt-2 text-gray-600">Loading PDFs...</p>
            </div>

            <div v-else-if="error" class="text-center py-8">
                <p class="text-red-600">{{ error }}</p>
            </div>

            <div v-else-if="Object.keys(pdfs).length === 0" class="text-center py-8">
                <p class="text-gray-600">No PDFs found for the selected date range.</p>
                <p class="text-gray-500 text-sm mt-2">Date range: {{ from || 'today' }} to {{ to || 'today' }}</p>
            </div>

            <div v-else class="space-y-6 overflow-y-auto">
                <div v-for="(datePdfsByTime, date) in pdfs" :key="date" class="border-b pb-4 last:border-b-0">
                    <h3 class="font-semibold text-lg mb-4">{{ formatDate(date) }}</h3>
                    <div v-for="(timeGroup, sortKey) in datePdfsByTime" :key="sortKey" class="mb-4 last:mb-0">
                        <h4 class="font-medium text-sm text-gray-600 mb-2">{{ timeGroup.displayTime }}</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div 
                                v-for="pdf in timeGroup.pdfs" 
                                :key="pdf.type + pdf.timestamp"
                                class="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                            >
                                <div class="flex items-center">
                                    <span class="text-sm font-medium">{{ pdf.label }}</span>
                                </div>
                                <div class="flex gap-2">
                                    <a 
                                        :href="pdf.url" 
                                        target="_blank"
                                        class="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                    >
                                        View
                                    </a>
                                    <a 
                                        :href="pdf.url + '?download=1'" 
                                        class="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                    >
                                        Download
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <jet-tertiary-button @click="close">
                Close
            </jet-tertiary-button>
        </template>
    </jet-dialog-modal>
</template>

<script>
import JetDialogModal from '@/Jetstream/DialogModal.vue';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import { DateTime } from "luxon";

export default {
    components: {
        JetDialogModal,
        JetTertiaryButton,
    },

    props: {
        show: {
            type: Boolean,
            default: false,
        },
        from: {
            type: String,
            default: null,
        },
        to: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            pdfs: {},
            loading: false,
            error: null,
        }
    },

    watch: {
        show(newVal) {
            if (newVal) {
                this.loadPdfs();
            }
        },
    },

    methods: {
        close() {
            this.$emit('close');
        },

        async loadPdfs() {
            // Default to today's date if no date range is provided
            const from = this.from || DateTime.now().toISODate();
            const to = this.to || DateTime.now().toISODate();

            this.loading = true;
            this.error = null;
            this.pdfs = {};

            try {
                const response = await axios.get(route('loads.pdfs.list'), {
                    params: {
                        from: from,
                        to: to,
                    }
                });

                this.pdfs = response.data.pdfs || {};
            } catch (error) {
                console.error('Error loading PDFs:', error);
                if (error.response) {
                    console.error('Error response:', error.response.data);
                    this.error = error.response.data.error || 'Failed to load PDFs. Please try again.';
                } else {
                    this.error = 'Failed to load PDFs. Please try again.';
                }
            } finally {
                this.loading = false;
            }
        },

        formatDate(dateString) {
            return DateTime.fromISO(dateString).toFormat('MMM d, yyyy');
        },
    },
}
</script>

